import { useRef, useEffect, useState } from 'react';
import { siteInfo } from '@/mocks/home';

export default function Hero() {
  const videoRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="hero" className="relative w-full min-h-screen flex items-center overflow-hidden bg-background-50">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-accent-100/40 via-background-50 to-primary-50/30"></div>
        <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-accent-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-[-100px] w-[500px] h-[500px] bg-primary-50/30 rounded-full blur-3xl"></div>
      </div>

      <div className="relative w-full px-4 md:px-6 py-24 md:py-32">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div
            ref={videoRef}
            className={`flex-1 w-full text-center lg:text-left transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 bg-accent-100 text-accent-700 px-4 py-1.5 rounded-full text-xs font-medium mb-5">
              <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span>下拉式阅读·沉浸追漫新体验</span>
            </div>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-foreground-900 leading-tight mb-4">
              耽耽漫画
              <br />
              <span className="text-primary-500">在线畅读·下拉随心</span>
            </h1>

            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6 max-w-xl mx-auto lg:mx-0">
              {siteInfo.description}
            </p>

            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-6 mb-8">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 font-heading">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href="#index"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line w-4 h-4 flex items-center justify-center"></i>
                开始阅读
              </a>
              <a
                href="#ranking"
                className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 border border-background-200/70 text-foreground-700 px-8 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-fire-line w-4 h-4 flex items-center justify-center"></i>
                人气排行
              </a>
            </div>
          </div>

          <div
            className={`flex-1 w-full max-w-lg lg:max-w-none transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-accent-200/30 via-primary-100/20 to-secondary-100/30 rounded-3xl blur-xl"></div>
              <div className="relative grid grid-cols-2 gap-3 md:gap-4">
                <div className="space-y-3 md:space-y-4 mt-6">
                  <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[3/4] relative group cursor-pointer">
                    <img
                      src="https://readdy.ai/api/search-image?query=Elegant%20ancient%20Chinese%20fantasy%20manga%20style%20illustration%20two%20handsome%20male%20characters%20in%20flowing%20robes%20one%20with%20long%20silver%20hair%20one%20with%20dark%20hair%20standing%20under%20cherry%20blossom%20tree%20moonlight%20atmosphere%20soft%20ethereal%20lighting%20warm%20color%20palette%20romantic%20mood%20detailed%20traditional%20Chinese%20clothing%20cinematic%20composition&width=600&height=800&seq=hero-img-01&orientation=portrait"
                      alt="古风仙侠漫画推荐"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-white text-xs font-medium">古风仙侠精选</span>
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[3/4] relative group cursor-pointer">
                    <img
                      src="https://readdy.ai/api/search-image?query=Modern%20urban%20romance%20manga%20style%20two%20stylish%20male%20characters%20in%20city%20street%20at%20night%20neon%20lights%20reflecting%20on%20wet%20pavement%20one%20wearing%20black%20leather%20jacket%20another%20in%20white%20shirt%20intimate%20eye%20contact%20warm%20amber%20streetlights%20cinematic%20noir%20feeling%20soft%20depth%20of%20field&width=600&height=800&seq=hero-img-03&orientation=portrait"
                      alt="现代都市漫画推荐"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-white text-xs font-medium">现代都市精选</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-3 md:space-y-4">
                  <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[3/4] relative group cursor-pointer">
                    <img
                      src="https://readdy.ai/api/search-image?query=School%20life%20romance%20manga%20style%20two%20teenage%20male%20characters%20in%20Japanese%20school%20uniforms%20sitting%20on%20rooftop%20at%20sunset%20golden%20hour%20warm%20orange%20sky%20one%20with%20glasses%20reading%20book%20another%20smiling%20at%20him%20cherry%20blossom%20petals%20floating%20soft%20nostalgic%20atmosphere%20clean%20illustration%20style&width=600&height=800&seq=hero-img-02&orientation=portrait"
                      alt="校园青春漫画推荐"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-white text-xs font-medium">校园青春精选</span>
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden bg-background-100 aspect-[3/4] relative group cursor-pointer">
                    <img
                      src="https://readdy.ai/api/search-image?query=Fantasy%20adventure%20manga%20style%20illustration%20two%20male%20warriors%20in%20elaborate%20armor%20standing%20on%20cliff%20edge%20overlooking%20magical%20kingdom%20one%20holding%20sword%20another%20casting%20spell%20dramatic%20clouds%20warm%20golden%20light%20breaking%20through%20epic%20scale%20detailed%20fantasy%20artwork%20cinematic%20composition&width=600&height=800&seq=hero-img-04&orientation=portrait"
                      alt="奇幻异世漫画推荐"
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                      <span className="text-white text-xs font-medium">奇幻异世精选</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <a href="#index" className="flex items-center justify-center w-10 h-10 rounded-full bg-background-100/80 hover:bg-background-200/80 transition-colors cursor-pointer">
          <i className="ri-arrow-down-s-line text-foreground-500 text-lg w-5 h-5 flex items-center justify-center"></i>
        </a>
      </div>
    </section>
  );
}